import * as React from 'react';
import * as styles from './Audio.css';
import { Controller } from '../controller/Controller';
import { IComposerAudio } from '../../../interfaces/IComposerAudio';
import { Helper } from '../../../Helper';
import { LocalizationService } from '../../../services/LocalizationService';
import { inject, observer } from 'mobx-react';
import { Store } from '../stores';
import { ComposerHelper } from '../helper/ComposerHelper';
import { IAComposerError } from '../placeholders/ComposerError';
import { IAComposerPlaceholder } from '../placeholders/ComposerPlaceholder';

export interface IProps {
  store?: Store;
  id: string;
  audio: IComposerAudio;
  index?: number;
  hideToolbox: boolean;
  showDropZones: boolean;
  isInMobileToolbox?: boolean;
  isCover?: boolean;
  onChange: (audio: IComposerAudio) => void;
  onFocus?: () => void;
  onDrag?: () => void;
  onDragEnd?: () => void;
}

export interface IState {
  showController: boolean;
  dragging: boolean;
  showDropZones: boolean;
  audioFormatError: boolean;
  audioUploadError: "too big" | "failed" | "";
  uploadProgressPercentage: number;
}

@inject('store')
@observer
export class Audio extends React.Component<IProps, IState> {

  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      showController: this.props.isInMobileToolbox ? true : false,
      dragging: false,
      showDropZones: false,
      audioFormatError: false,
      audioUploadError: "",
      uploadProgressPercentage: undefined
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    document.body.addEventListener('onmousedown', (event: any) => {
      var exceptions = '#IA_audio *, #IA_toolboxContent *, #IA_OrgAssetsUploader *, #IA_StockPhotos *';
      console.log(this.props.store.activeComponent);
      if (!event.target.matches(exceptions) && this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id) {
        this.hideToolbox()
        this.props.store.activeComponent = undefined;
      }
    });
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.hideToolbox) {
      this.hideToolbox();
    }
    if (props.showDropZones) {
      this.hideToolbox()
    }
    if (this.props.hideToolbox !== props.hideToolbox && props.hideToolbox != undefined && !props.hideToolbox) {
      this.setState({ showController: true });
    }
    if (props.showDropZones !== this.props.showDropZones) {
      setTimeout(() => {
        this.setState({ showDropZones: props.showDropZones });
      }, 0);
    }
    if (props.audio?.url?.length > 0 && !props.audio?.url?.toLowerCase().includes(".mp3")) {
      this.setState({ audioFormatError: true });
    } else {
      this.setState({ audioFormatError: false });
    }
  }

  private showToolbox(): void {
    const editor = document.getElementById(this.props.id);
    const toolbox: any = editor ? editor.getElementsByClassName("ql-toolbar")[0] : undefined;
    if (toolbox) {
      toolbox.style.display = "block";
    }
    this.setState({ showController: true });
  }

  private hideToolbox(): void {
    const editor = document.getElementById(this.props.id);
    const toolbox: any = editor ? editor.getElementsByClassName("ql-toolbar")[0] : undefined;
    if (toolbox) {
      toolbox.style.display = "none";
    }
    this.setState({ showController: false });
  }

  private setStateAndUpdateMessage(file: IComposerAudio): void {
    if (file.status !== "success") {
      file.status === "too big" ? this.setState({ audioUploadError: "too big" }) : this.setState({ audioUploadError: "failed" });
    }
    file.status = "success";
    this.props.onChange(file);
  }

  private getAudioStatus(): "success" | "waiting" | "error" {
    if (this.props.audio.status === "failed"
      || this.props.audio.status === "too big"
      || this.props.audio.status === "duplicate"
      || this.state.audioFormatError
      || this.state.audioUploadError !== ""
    ) {
      return "error";
    }
    if (!this.props.audio.url || this.props.audio.status === "waiting" || this.props.audio.status === "uploading") {
      return "waiting";
    }
    return "success";
  }

  private getPlaceholderText(): string {
    if (this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id) {
      if (this.props.audio?.source === "Upload") return this.localizationService.strings.Composer_DropAudioHere;
      if (this.props.audio?.source === "Link") return this.localizationService.strings.Composer_PasteAAudioURL;
    }
    return this.localizationService.strings.Composer_PasteAAudioURL;
  }

  private getErrorText(): string {
    if (this.state.audioFormatError) {
      return this.localizationService.strings.Composer_AudioFormatError;
    }
    if (this.state.audioUploadError === "too big") {
      return this.localizationService.strings.Composer_AudioUploadSizeError;
    }
    return this.localizationService.strings.Composer_AudioUploadGenericError;
  }

  private audioOnClick(): void {
    setTimeout(() => {
      this.props.onFocus();
      this.showToolbox();
    }, 100);
  }

  private onUploadProgress(progressPercentage: number) {
    this.setState({ uploadProgressPercentage: progressPercentage });
  }

  private finalizeUpload() {
    this.props.audio.fileRef = null;
    this.setStateAndUpdateMessage(this.props.audio);
    this.props.store.isUploadingVideoOrImage = false;
    this.setState({ uploadProgressPercentage: undefined });
  }

  public render(): JSX.Element {
    const audioStatus = this.getAudioStatus();
    let showController = this.state.showController && !this.state.showDropZones;
    if (showController && this.props.isCover && !this.props.store.activeComponent?.audio?.url) showController = false;
    const height = this.props.store.isMobile ? (this.state.showDropZones ? ((screen.width - 100) / 16 * 9) + 55 : (screen.width - 100) / 16 * 9) : (this.state.showDropZones ? 41 : 54);
    let audioUrl = this.props.audio.url;
    return (
      <div
        id={"IA_audio"}
        className={styles.IA_audio}
      >
        {((this.props.store.isMobile && this.props.isInMobileToolbox) || !this.props.store.isMobile) &&
          <Controller
            visible={showController}
            highlightColor={this.props.store.highlightColor}
            onDublicate={!this.props.isInMobileToolbox && !this.props.isCover ? () => this.props.store.dublicateComponent() : undefined}
            onDelete={() => {
              if (this.props.store.activeComponent.audio && this.props.store.activeComponent.audio.url) {
                this.props.store.activeComponent.audio = {
                  source: "Link"
                };
              } else if (!this.props.isCover) {
                this.props.store.removeComponentAtIndex(this.props.index);
                this.hideToolbox()
                this.props.store.activeComponent = undefined;
              }
              this.forceUpdate();
            }}
            onDrag={this.props.onDrag ? () => this.props.onDrag() : undefined}
            onDragEnd={this.props.onDragEnd ? () => this.props.onDragEnd() : undefined}
          />
        }
        {this.state.showDropZones && this.props.index === 0 && !this.state.showController &&
          <div
            style={{
              marginBottom: 10
            }}
            className={styles.IA_dropZones}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDragEnter={(event) => {
              event.preventDefault();
            }}
            onDropCapture={(event) => {
              event.preventDefault();
              if (this.props.store.startIndex != undefined) {
                this.props.store.moveComponentAtIndex(0);
              } else {
                this.props.store.addComponentAtIndex(0);
              }
            }}
          />
        }
        <div style={{ width: "100%", height: "100%", float: "left" }}>
          {audioStatus === "error" &&
            <IAComposerError
              errorMessage={this.getErrorText()}
              // errorLink={this.getErrorLink()}
              onClick={() => this.audioOnClick()}
              borderRadius={5}
            />
          }
          {audioStatus === "waiting" &&
            <IAComposerPlaceholder
              id={Helper.getRandomStringKey()}
              enableUpload={this.props.audio.source === "Upload"}
              isActive={this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id}
              highlightColor={this.props.store.highlightColor}
              icon={{
                name: "Audio",
                size: 40
              }}
              showSpinner={false}
              progressBarText={this.localizationService.strings.Composer_Uploading}
              progressBarPercentage={this.state.uploadProgressPercentage}
              text={this.getPlaceholderText()}
              fileType="audio"
              uploadButtonText={this.localizationService.strings.Composer_SelectFromMediaLibrary}
              borderRadius={5}
              uploadFileCallback={async (file) => {
                this.props.store.isUploadingVideoOrImage = true;
                let site: string;
                let serverRelativeUrl: string;
                if (this.props.audio.organizationalAssetPath) {
                  site = this.props.audio.organizationalAssetPath.split("/")[2];
                  serverRelativeUrl = this.props.audio.organizationalAssetPath.split(`/${site}/`)[1];
                }
                ComposerHelper.uploadFileDirectlyToSharepoint(this.props.store.environment, file, this.props.store.tenant, this.props.store.token, (progress) => this.onUploadProgress(progress), site, serverRelativeUrl)
                  .then(fileUrl => {
                    setTimeout(() => {
                      // wait for progress bar animation to finish
                      this.setState({ uploadProgressPercentage: undefined });
                      this.props.audio.status = "success";
                      this.props.audio.url = `${fileUrl?.toLowerCase().split(".mp3")[0]}.mp3`;
                      this.finalizeUpload();
                    }, 500);
                  }).catch(() => {
                    this.props.audio.status = "failed";
                    this.finalizeUpload();
                  });
              }}
              onClick={() => this.audioOnClick()}
            />
          }
          {audioStatus === "success" &&
            <>
              <audio
                controls
                style={{
                  width: "100%",
                }}
              >
                <source src={audioUrl} type="audio/mp3" />
              </audio>
              <div
                className={styles.IA_iframeOverlay}
                style={{ marginTop: -height }}
                onClick={() => this.audioOnClick()}
              />
            </>
          }
        </div>
        {this.state.showDropZones && !this.state.showController &&
          <div
            className={styles.IA_dropZones}
            style={{
              marginTop: 10
            }}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDragEnter={(event) => {
              event.preventDefault();
            }}
            onDropCapture={(event) => {
              event.preventDefault();
              event.preventDefault();
              if (this.props.store.startIndex != undefined) {
                this.props.store.moveComponentAtIndex(this.props.index + 1);
              } else {
                this.props.store.addComponentAtIndex(this.props.index + 1);
              }
            }}
          />
        }
      </div >
    );
  }


}